//! Importing libraries
import axios from "axios";

//* Importing components
import {
  Form,
  FormControl as Input,
  InputGroup,
  Button,
} from "react-bootstrap";

//* Importing icons
import { SaveRounded } from "@material-ui/icons";

//* Importing JodiEditor
import JoditEditor from "jodit-react";

//! Importing hooks
import { useState, useEffect } from "react";

//* Importing icons from react-icons
import { FaListAlt } from "react-icons/fa";
import { BsFillEyeFill } from "react-icons/bs";
import { MdDelete } from "react-icons/md";

//! Importing custom hooks
import { useDisciplines } from "../../../hooks/disciplines/useDisciplines";

//* Importing DOMPurify
import DOMPurify from "dompurify";

//* Importing Custom component
import CustomAlert from "../../../component/base/CustomAlert";

//* Importing react-redux hooks
import { useSelector } from "react-redux";

//* Importing react-router-dom components
import { useNavigate, useParams } from "react-router-dom";

const editorConfig = {
  readonly: false,
  toolbar: true,
};

type Question = {
  question_content: string;
  correct: string;
};

type Alternativa = {
  label: string;
  value: string;
  correct?: boolean;
};

type QuestionId = string | undefined;

function sanitizeInput(input: string): string {
  return DOMPurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
}

export function QuestionsPublicEdit() {
  const [enunciado, setEnunciado] = useState<string>("");
  const [alternativas, setAlternativas] = useState<Alternativa[]>([
    { label: "a", value: "" },
    { label: "b", value: "" },
    { label: "c", value: "" },
    { label: "d", value: "" },
    { label: "e", value: "" },
  ]);
  const [numAlternativas, setNumAlternativas] = useState<number>(1);
  const [tipoQuestao, setTipoQuestao] = useState<string>("objective");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [justificativa, setJustificativa] = useState<string>("");
  const [alternativaCorreta, setAlternativaCorreta] = useState<string>("");
  const [disciplina, setDisciplina] = useState<string>("");
  const [nivelEscolaridade, setNivelEscolaridade] = useState<string>("");
  const [nivelDificuldade, setNivelDificuldade] = useState<string>("");
  const token = useSelector((state: any) => state.auth.token);
  const { discipline } = useDisciplines(token);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { id } = useParams();
  const logs = useSelector((state: any) => state.log.logs);

  const navigate = useNavigate();

  const handleBack = () => {
    navigate("/public-questions");
  };

  useEffect(() => {
    // if id exists, fetch question data
    if (id) {
      const fetchQuestion = async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URI}/admin/private/user-questions/${id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const data = response.data.data;

          setEnunciado(data.statement);
          setTipoQuestao(data.question_type);
          setDisciplina(data.discipline_id);
          setNivelEscolaridade(data.education_level);
          setNivelDificuldade(data.difficulty_level);
          setJustificativa(data.justify_answer_text);
          setAlternativaCorreta(
            data.alternatives?.find((a: any) => a.correct)?.alternative || ""
          );

          if (data.alternatives) {
            const validAlternatives = data.alternatives
              .filter(
                (alt: { alternative_content: string }) =>
                  alt.alternative_content.trim() !== ""
              )
              .map(
                (alt: {
                  alternative: string;
                  alternative_content: string;
                  correct: boolean;
                }) => ({
                  label: alt.alternative,
                  value: alt.alternative_content,
                  correct: alt.correct,
                })
              );

            setAlternativas(validAlternatives);
            setNumAlternativas(validAlternatives.length);
          }
        } catch (error) {
          console.error("Erro ao carregar a questão:", error);
          setErrorMessage("Não foi possível carregar os dados da questão.");
        }
      };

      fetchQuestion();
    }
  }, [id, token]);

  useEffect(() => {
    const startIndex = logs.findIndex(
      (log: string) => log === "__QUESTION_DATA_START__"
    );
    const endIndex = logs.findIndex(
      (log: string) => log === "__QUESTION_DATA_END__"
    );

    if (startIndex !== -1 && endIndex !== -1) {
      const questionData = logs.slice(startIndex + 1, endIndex);
      console.log("Question Data from Logs:", questionData);

      questionData.forEach((log: string) => {
        const [key, value] = log.split(":");
        switch (key) {
          case "statement":
            setEnunciado(value);
            break;
          case "alternatives":
            const alts = value.split(", ");
            const newAlternatives = alts.map((alt: string, index: number) => ({
              label: String.fromCharCode(97 + index),
              value: alt,
            }));
            setAlternativas(newAlternatives);
            setNumAlternativas(alts.length);
            break;
          case "solution":
            const correctAlternative = value.match(/^([a-e])\)/)?.[1];
            if (correctAlternative) {
              setAlternativaCorreta(correctAlternative);
            }
            setJustificativa(value);
            break;
          case "answer":
            setJustificativa(value);
            break;
          case "educationlevel":
            const educationMap: { [key: string]: string } = {
              fundamental: "fundamental",
              medio: "medio",
              superior: "superior",
            };
            const newEducationLevel =
              educationMap[value] || value.toLowerCase();
            setNivelEscolaridade(newEducationLevel);
            break;
          case "discipline":
            const disciplineObj = discipline.find((d) => d.id === value);
            if (disciplineObj) {
              setDisciplina(disciplineObj.id);
            }
            break;
          case "questiontype":
            const typeMap: { [key: string]: string } = {
              "Questão objetiva": "objective",
              "Questão dissertativa": "dissertation",
            };
            const newQuestionType = typeMap[value] || value;
            setTipoQuestao(newQuestionType);
            break;
          case "alternativescount":
            const newAlternativesCount = parseInt(value);
            setNumAlternativas(newAlternativesCount);
            break;
          case "difficultylevel":
            const difficultyMap: { [key: string]: string } = {
              facil: "facil",
              medio: "medio",
              dificil: "dificil",
            };
            const newDifficultyLevel =
              difficultyMap[value] || value.toLowerCase();
            setNivelDificuldade(newDifficultyLevel);
            break;
        }
      });
    }
  }, [logs, discipline]);

  const renderAlternativas = () => {
    return alternativas.slice(0, numAlternativas).map((alt, index) => (
      <InputGroup key={alt.label}>
        <div className="flex fdcolumn flex1 margin-bottom">
          <Form.Label>
            Alternativa {alt.label}) <span style={{ color: "red" }}>*</span>
          </Form.Label>
          <JoditEditor
            value={alt.value}
            config={editorConfig}
            onBlur={(newContent) =>
              setAlternativas((prev) =>
                prev.map((a, i) =>
                  i === index ? { ...a, value: sanitizeInput(newContent) } : a
                )
              )
            }
          />
          {missingFields.includes("alternativas") && !alt.value && (
            <CustomAlert variant="danger">Campo obrigatório</CustomAlert>
          )}
        </div>
      </InputGroup>
    ));
  };

  const deleteFromAIBank = async (statement: string) => {
    try {
      const encodedStatement = encodeURIComponent(statement.trim());

      const searchResponse = await fetch(
        `${process.env.REACT_APP_API_URI}/admin/private/ai-question-banks?search=${encodedStatement}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!searchResponse.ok) {
        throw new Error(`Search failed with status: ${searchResponse.status}`);
      }

      const searchResult = await searchResponse.json();

      if (
        searchResult.success &&
        searchResult.data &&
        searchResult.data.length > 0
      ) {
        const questionToDelete = searchResult.data[0];

        const deleteResponse = await fetch(
          `${process.env.REACT_APP_API_URI}/admin/private/ai-question-banks/${questionToDelete.id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!deleteResponse.ok) {
          throw new Error(
            `Delete failed with status: ${deleteResponse.status}`
          );
        }

        return true;
      } else {
        console.warn("No matching question found to delete");
        return false;
      }
    } catch (error) {
      console.error("Error in deleteFromAIBank:", error);
      throw error;
    }
  };

  const saveQuestion = async () => {
    const baseData = {
      discipline_id: disciplina,
      question_type: tipoQuestao,
      education_level: nivelEscolaridade,
      difficulty_level: nivelDificuldade,
      statement: enunciado,
      alternatives:
        tipoQuestao !== "objective"
          ? null
          : alternativas.slice(0, numAlternativas).map((alt) => ({
              alternative: alt.label,
              alternative_content: alt.value,
              correct: alternativaCorreta === alt.label,
            })),
      justify_answer_text: justificativa,
      number_of_alternatives: tipoQuestao !== "objective" ? 0 : numAlternativas,
      usage_status: true,
    };

    try {
      let questionId: QuestionId;

      // 1. Primeiro salva a questão para obter o ID
      if (id) {
        // Caso de edição
        await axios.put(
          `${process.env.REACT_APP_API_URI}/admin/private/user-questions/${id}`,
          baseData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        questionId = id;
      } else {
        // Caso de criação
        const response = await axios.post(
          `${process.env.REACT_APP_API_URI}/admin/private/user-questions`,
          baseData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        questionId = response.data.data.id;
      }

      navigate("/questions");
    } catch (error) {
      console.error("Erro ao salvar questão:", error);
      setErrorMessage("Erro ao salvar a questão. Por favor, tente novamente.");
    }
  };

  const validateFields = () => {
    const missing: string[] = [];
    if (!tipoQuestao) missing.push("tipoQuestao");
    if (!enunciado) missing.push("enunciado");
    if (!justificativa) missing.push("justificativa");
    if (!alternativaCorreta && tipoQuestao === "objective")
      missing.push("alternativaCorreta");
    if (!disciplina) missing.push("disciplina");
    if (!nivelEscolaridade) missing.push("nivelEscolaridade");
    if (!nivelDificuldade) missing.push("nivelDificuldade");
    if (
      tipoQuestao === "objective" &&
      alternativas.slice(0, numAlternativas).some((alt) => !alt.value)
    )
      missing.push("alternativas");
    setMissingFields(missing);
    return missing.length === 0;
  };

  const handleSave = async () => {
    if (!validateFields()) {
      setErrorMessage("Por favor, preencha todos os campos obrigatórios.");
      return;
    }

    const dataToSave = {
      question_type: tipoQuestao,
      discipline_id: disciplina,
      education_level: nivelEscolaridade,
      difficulty_level: nivelDificuldade,
      statement: enunciado,
      alternatives:
        tipoQuestao !== "objective"
          ? null
          : alternativas.slice(0, numAlternativas).map((alt) => ({
              alternative: alt.label,
              alternative_content: alt.value,
              correct: alternativaCorreta === alt.label,
            })),
      justify_answer_text: justificativa,
      number_of_alternatives: tipoQuestao !== "objective" ? 0 : numAlternativas,
    };

    try {
      let questionId = id;

      if (id) {
        //! Edição da questão
        await axios.put(
          `${process.env.REACT_APP_API_URI}/admin/private/user-questions/${id}`,
          dataToSave,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        //! Criação da questão
        const response = await axios.post(
          `${process.env.REACT_APP_API_URI}/admin/private/user-questions`,
          dataToSave,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        questionId = response.data.data.id; //! Get question id
      }

      navigate("/public-questions");
    } catch (error) {
      console.error("Erro ao salvar questão:", error);
      setErrorMessage("Erro ao salvar a questão. Por favor, tente novamente.");
    }
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="py-5 px-3">
          <div className="card-body" id="card">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4
                  className="mt-0 header-title"
                  style={{ fontSize: "1.5rem", color: "black" }}
                >
                  {id ? "Editar Questão" : "Cadastrar Questão"}
                </h4>
                <p style={{ fontSize: "1rem", color: "black" }}>
                  {id
                    ? "Edite os dados da questão no sistema"
                    : "Cadastre suas questões no sistema"}
                </p>
              </div>
            </div>
            <br />

            <Form style={{ position: "relative" }}>
              <InputGroup>
                <div className="flex fdcolumn flex1 margin-bottom">
                  <Form.Label>
                    Tipo de questão <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <select
                    className="form-select"
                    value={tipoQuestao}
                    onChange={(e) => setTipoQuestao(e.target.value)}
                  >
                    <option value={"objective"}>Questão objetiva</option>
                    <option value={"set"}>
                      Conjuntos de questões objetivas com enunciado em comum
                    </option>
                    <option value={"dissertation"}>Questão dissertativa</option>
                  </select>
                  {missingFields.includes("tipoQuestao") && (
                    <CustomAlert variant="danger">
                      Campo obrigatório
                    </CustomAlert>
                  )}
                </div>
              </InputGroup>
              <InputGroup>
                <div className="flex fdcolumn flex1 margin-bottom">
                  <Form.Label>
                    Disciplina <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <select
                    className="form-select"
                    value={disciplina}
                    onChange={(e) => setDisciplina(e.target.value)}
                  >
                    <option value={""}>Selecione uma disciplina</option>
                    {discipline.map((disciplina: any) => (
                      <option key={disciplina.id} value={disciplina.id}>
                        {disciplina.name}
                      </option>
                    ))}
                  </select>
                  {missingFields.includes("disciplina") && (
                    <CustomAlert variant="danger">
                      Campo obrigatório
                    </CustomAlert>
                  )}
                </div>
              </InputGroup>
              <div className="row">
                <InputGroup className="col-lg">
                  <div className="flex fdcolumn flex1 margin-bottom">
                    <Form.Label>
                      Nível de Escolaridade{" "}
                      <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <select
                      className="form-select"
                      value={nivelEscolaridade}
                      onChange={(e) => setNivelEscolaridade(e.target.value)}
                    >
                      <option value={""}>
                        Selecione um nível de escolaridade
                      </option>
                      <option value={"fundamental"}>Ensino Fundamental</option>
                      <option value={"medio"}>Ensino Médio</option>
                      <option value={"superior"}>Ensino Superior</option>
                    </select>
                    {missingFields.includes("nivelEscolaridade") && (
                      <CustomAlert variant="danger">
                        Campo obrigatório
                      </CustomAlert>
                    )}
                  </div>
                </InputGroup>
                <InputGroup className="col-lg">
                  <div className="flex fdcolumn flex1 margin-bottom">
                    <Form.Label>
                      Nível de Dificuldade{" "}
                      <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <select
                      className="form-select"
                      value={nivelDificuldade}
                      onChange={(e) => setNivelDificuldade(e.target.value)}
                    >
                      <option value={""}>
                        Selecione um nível de dificuldade
                      </option>
                      <option value={"facil"}>Fácil</option>
                      <option value={"medio"}>Médio</option>
                      <option value={"dificil"}>Difícil</option>
                    </select>
                    {missingFields.includes("nivelDificuldade") && (
                      <CustomAlert variant="danger">
                        Campo obrigatório
                      </CustomAlert>
                    )}
                  </div>
                </InputGroup>
              </div>

              <InputGroup>
                <div className="flex fdcolumn flex1 margin-bottom">
                  <Form.Label>
                    Enunciado <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <JoditEditor
                    value={enunciado}
                    config={editorConfig}
                    onBlur={(newContent) =>
                      setEnunciado(sanitizeInput(newContent))
                    }
                  />
                  {missingFields.includes("enunciado") && (
                    <CustomAlert variant="danger">
                      Campo obrigatório
                    </CustomAlert>
                  )}
                </div>
              </InputGroup>
              <br />
              {tipoQuestao === "objective" && (
                <div>
                  <InputGroup>
                    <div className="flex fdcolumn flex1 margin-bottom">
                      <Form.Label>
                        Número de alternativas{" "}
                        <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <select
                        className="form-select"
                        value={numAlternativas}
                        onChange={(e) =>
                          setNumAlternativas(parseInt(e.target.value))
                        }
                      >
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                        <option value={4}>4</option>
                        <option value={5}>5</option>
                      </select>
                    </div>
                  </InputGroup>
                  {renderAlternativas()}
                  <InputGroup>
                    <div className="flex fdcolumn flex1 margin-bottom">
                      <Form.Label>
                        Alternativa correta{" "}
                        <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <select
                        className="form-select"
                        value={alternativaCorreta}
                        onChange={(e) => setAlternativaCorreta(e.target.value)}
                      >
                        <option value="">
                          Selecione uma alternativa correta
                        </option>
                        {Array.from({ length: numAlternativas }, (_, i) => (
                          <option key={i} value={String.fromCharCode(97 + i)}>
                            {String.fromCharCode(97 + i)}
                          </option>
                        ))}
                      </select>
                      {missingFields.includes("alternativaCorreta") && (
                        <CustomAlert variant="danger">
                          Campo obrigatório
                        </CustomAlert>
                      )}
                    </div>
                  </InputGroup>
                </div>
              )}
              {tipoQuestao === "set" && (
                <div>
                  <br />
                  <InputGroup>
                    <div
                      className="flex justify-content-end flex-wrap mb-4"
                      style={{ width: "100%" }}
                    >
                      <Button
                        onClick={() => {}}
                        variant="success"
                        className="flex align-center jcc"
                      >
                        <FaListAlt style={{ marginRight: 10 }} />{" "}
                        <p style={{ margin: 0 }}>Adicionar questão</p>
                      </Button>
                    </div>
                  </InputGroup>
                </div>
              )}
              {tipoQuestao === "set" && (
                <InputGroup>
                  <div className="flex fdcolumn flex1 margin-bottom col-12">
                    <div className="table-rep-plugin">
                      <div
                        className="table-responsive mb-0"
                        data-pattern="priority-columns"
                      >
                        <table
                          id="tech-companies-1"
                          className="table table-striped"
                        >
                          <thead>
                            <tr>
                              <th>Enunciado</th>
                              <th>Alternativa Correta</th>
                              <th data-priority="3">Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {questions.map((item, idx) => (
                              <tr key={idx}>
                                <td>
                                  <p className="text_overflow">
                                    {item.question_content}
                                  </p>
                                </td>
                                <td>{item.correct})</td>
                                <td>
                                  <span
                                    onClick={() => {}}
                                    className="inline-flex align-center jcc bg-warning icon"
                                    style={{
                                      width: "2em",
                                      height: "2em",
                                      borderRadius: "7px",
                                    }}
                                    title="Gerenciar questão"
                                  >
                                    <BsFillEyeFill size={17} color="white" />
                                  </span>
                                  <span
                                    onClick={() =>
                                      setQuestions(
                                        questions.filter(
                                          (_, id2) => idx !== id2
                                        )
                                      )
                                    }
                                    className="inline-flex align-center jcc bg-danger icon"
                                    style={{
                                      width: "2em",
                                      height: "2em",
                                      borderRadius: "7px",
                                    }}
                                    title="Deletar questão"
                                  >
                                    <MdDelete size={17} color="white" />
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {questions.length === 0 && (
                      <div className="row w-100">
                        <div className="col-12">
                          <p style={{ textAlign: "center" }}>
                            Nenhuma questão adicionada
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </InputGroup>
              )}
              <InputGroup>
                <div className="flex fdcolumn flex1 margin-bottom">
                  <Form.Label>
                    Justificativa <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Input
                    placeholder="Justificativa explicando a resposta da questão"
                    as="textarea"
                    value={justificativa}
                    onChange={(e) => setJustificativa(e.target.value)}
                  />
                  {missingFields.includes("justificativa") && (
                    <CustomAlert variant="danger">
                      Campo obrigatório
                    </CustomAlert>
                  )}
                </div>
              </InputGroup>
              <br />
              <div className="w100 inline-flex jcsb mt-3">
                <Button
                  variant="secondary"
                  className="flex align-center jcc"
                  onClick={handleBack}
                >
                  Voltar
                </Button>
                <Button
                  variant="success"
                  type="button"
                  className="flex align-center jcc"
                  onClick={saveQuestion}
                >
                  <SaveRounded style={{ marginRight: 10 }} /> Salvar
                </Button>
              </div>
              {missingFields.length > 0 && (
                <div style={{ marginTop: "20px" }}>
                  <CustomAlert variant="danger">
                    Por favor, preencha todos os campos obrigatórios.
                  </CustomAlert>
                </div>
              )}
              {errorMessage && (
                <div style={{ marginTop: "20px" }}>
                  <CustomAlert variant="danger">{errorMessage}</CustomAlert>
                </div>
              )}
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}
